import { getIsolatedAssociationKeysFromClassNameModelUtil } from 'models/utils/get-isolated-association-keys'
import openErrorBannerAction from 'modules/banner/actions/open-error'
import REDUX_ACTION from 'modules/redux/constants/action'
import REDUX_OPTION from 'modules/redux/constants/option'
import loadReduxApi from 'modules/redux/services/load'
import getAssociationKeysReduxUtil from 'modules/redux/utils/get-associations-keys'
import getDefaultDomainListPathFromPath from 'modules/redux/utils/get-default-domain-list-path-from-domain-path'
import isInstanceReduxUtil from 'modules/redux/utils/is-instance'
import isLoadingReduxUtil from 'modules/redux/utils/is-loading'
import { isLoadedReduxUtil } from 'modules/redux/utils/legacy/is-loaded'
import { getObjectUtil } from 'packages/src'

/**
 * Checks whether all the instance's associations are loaded.
 * @param  {Instance}  instance
 * @param  {String[]}  associationKeys
 * @returns {boolean}
 */
function isAllAssociationsLoadedReduxUtil (instance, associationKeys) {
  return associationKeys.every(associationKey => {
    if (Array.isArray(instance.data[associationKey])) {
      return instance.data[associationKey].every(isLoadedReduxUtil)
    }
    return isLoadedReduxUtil(instance.data[associationKey])
  })
}

/**
 * Checks whether the given instance and its associations are loaded.
 * @param  {Instance|null}  instance
 * @returns {boolean}
 */
function isFullyLoadedReduxUtil (instance) {
  const className = getObjectUtil(instance, 'className')
  let assocationKeys = getAssociationKeysReduxUtil(className)
  if (!assocationKeys) {
    return isLoadedReduxUtil(instance)
  }
  // Remove association keys that can be loaded separately
  const isolatedAssociationKeys = getIsolatedAssociationKeysFromClassNameModelUtil(className)
  assocationKeys = assocationKeys.filter(assoc => !isolatedAssociationKeys.includes(assoc))
  return (
    isLoadedReduxUtil(instance) &&
    isAllAssociationsLoadedReduxUtil(instance, assocationKeys)
  )
}

/**
 * Loads a instance from the server.
 * it first tries to load the domain (first batch) then the instance itself.
 * @private
 * @param  {Object}  data
 *         {string}  path
 *         {string}  endpoint
 *         {string}  [domainMapValue]
 *         {string}  domainListPath
 *         {string}  [instanceKey]
 *         {string}  [remoteUrl]
 *         {Object}  options
 *         {boolean} [REDUX_OPTION.FORCED]
 *         {boolean} [REDUX_OPTION.SHALLOW_PARSING]
 *         {Object}  [APP_OPTION.JWT]
 * @returns {Function}
 */
function loadInstance (data) {
  return (dispatch, getState) => {
    let {
      path,
      endpoint,
      domainMapValue,
      domainListPath,
      instanceKey,
      options,
      remoteUrl
    } = data

    // Get the instance
    let instance = getObjectUtil(getState(), path)
    if (!isInstanceReduxUtil(instance)) {
      path = `${path}.data.${domainMapValue}`
    }
    instance = getObjectUtil(getState(), path)

    // The instance is already loading
    if (isLoadingReduxUtil(instance) && !instanceKey) {
      return
    }

    // The instance is already loaded
    if (
      isFullyLoadedReduxUtil(instance) &&
      !options[REDUX_OPTION.FORCED] &&
      !instanceKey
    ) {
      return
    }

    return dispatch(
      callApiLoad(
        {
          endpoint,
          options,
          path,
          domainMapValue,
          domainListPath,
          instanceKey,
          remoteUrl
        }))
  }
}

/**
 * Loads a DomainList.
 * @private
 * @param  {Object}  data
 *         {string}  path
 *         {string}  endpoint
 *         {string}  domainListPath
 *         {string}  remoteUrl
 *         {string}  [className]
 *         {Object}  options
 *         {boolean} [REDUX_OPTION.FORCED]
 *         {boolean} [REDUX_OPTION.SHALLOW_PARSING]
 *         {Object}  [APP_OPTION.JWT]
 * @returns {Function}
 */
function loadDomainList (data) {
  return (dispatch, getState) => {
    const {
      path,
      endpoint,
      domainListPath,
      className,
      options,
      remoteUrl
    } = data

    const domainList = getObjectUtil(getState(), domainListPath)

    // If the DomainList is already loading, noop
    if (
      isLoadingReduxUtil(domainList) &&
      !options[REDUX_OPTION.FORCED]
    ) {
      return
    }
    // If the DomainList is already loaded, noop
    if (
      isLoadedReduxUtil(domainList) &&
      !options[REDUX_OPTION.FORCED]
    ) {
      return
    }
    // Mark the DomainList as loading and call the api
    return dispatch(
      callApiLoad(
        {
          endpoint,
          options,
          path,
          domainListPath,
          className,
          remoteUrl
        }))
  }
}

/**
 * Marks the given `Instance`, `DomainList`, `DomainInstance` as loading,
 * then call the api to load it.
 * @private
 * @param  {Object}  data
 *         {string}  path
 *         {string}  endpoint
 *         {string}  [className]
 *         {string}  [domainMapValue]
 *         {string}  [domainListPath]
 *         {string}  [instanceKey]
 *         {string}  [remoteUrl]
 *         {Object}  options
 *         {boolean} options[REDUX_OPTION.FORCED]
 *         {boolean} options[REDUX_OPTION.SHALLOW_PARSING]
 *         {Object}  options[APP_OPTION.JWT]
 * @returns {Function}
 */
function callApiLoad (data) {
  return dispatch => {
    const {
      path,
      endpoint,
      domainListPath,
      domainMapValue,
      instanceKey,
      className,
      remoteUrl,
      options
    } = data
    // Mark the `DomainInstance` or DomainList` as loading
    dispatch(
      loadingLegacyReduxAction(
        {
          path,
          domainListPath,
          domainMapValue,
          options
        })
    )
    return loadReduxApi({
      endpoint,
      remoteUrl,
      options
    })
      .then(data => {
        dispatch(
          loadSuccessLegacyReduxAction(
            {
              path,
              data,
              domainListPath,
              instanceKey,
              options,
              className
            }))
        return data
      })
      .catch(error => {
        dispatch(
          loadErrorLegacyReduxAction(
            {
              path,
              error,
              domainListPath,
              options
            }))
        if (!options[REDUX_OPTION.SHOULD_HIDE_BANNER]) {
          dispatch(
            openErrorBannerAction(error)
          )
        }
        // assume action create catch errors
        throw error
      })
  }
}

/**
 * Marks the given `Instance`, `DomainList`, `DomainInstance` as loading.
 * @param  {Object} data
 *         {string} path
 *         {string} [domainMapValue]
 *         {string} [domainListPath]
 *         {Object} options
 *         {Object} options[REDUX_OPTION.FORCED]
 *         {Object} options[REDUX_OPTION.SHALLOW_PARSING]
 *         {Object} options[APP_OPTION.JWT]
 * @returns {Function}
 */
function loadingLegacyReduxAction (data) {
  const {
    path,
    domainMapValue,
    domainListPath,
    options
  } = data
  return {
    type: REDUX_ACTION.LOAD,
    payload: {
      path,
      domainListPath,
      domainMapValue,
      options
    }
  }
}

/**
 * Marks the given `Instance`, `DomainList`, `DomainInstance` as loaded.
 * @param  {Object}  data
 *         {string}  path
 *         {Object}  data
 *         {string}  [className]
 *         {string}  [instanceKey]
 *         {string}  [domainListPath]
 *         {Object}  options
 *         {boolean} options[REDUX_OPTION.FORCED]
 *         {boolean} options[REDUX_OPTION.SHALLOW_PARSING]
 *         {Object}  options[APP_OPTION.JWT]
 * @returns {Function}
 */
function loadSuccessLegacyReduxAction (payload) {
  return {
    type: REDUX_ACTION.LOAD_SUCCESS,
    payload: payload
  }
}

/**
 * Marks the given `Instance`, `DomainList`, `DomainInstance` as errored.
 * @param  {Object}  data
 *         {string}  path
 *         {Object}  error
 *         {string}  className
 *         {string}  [domainListPath]
 *         {Object}  options
 *         {boolean} [REDUX_OPTION.FORCED]
 *         {boolean} [REDUX_OPTION.SHALLOW_PARSING]
 *         {Object}  [APP_OPTION.JWT]
 * @returns {Function}
 */
function loadErrorLegacyReduxAction ({
  path,
  error,
  domainListPath,
  options
}) {
  return {
    type: REDUX_ACTION.LOAD_ERROR,
    payload: {
      error,
      path,
      domainListPath,
      options
    }
  }
}

/**
 * Loads a `Domain` or `Instance` from the server.
 * it first tries to load the domain (first batch) then the instance itself.
 *
 * @param  {Object}  params
 *         {string}  path
 *         {string}  endpoint
 *         {string}  [className]
 *         {string}  [remoteUrl]
 *         {string}  [domainMapValue]
 *         {string}  [domainListPath]
 *         {string}  [instanceKey]
 *         {Object}  [options={}]
 *         {boolean} [options[REDUX_OPTION.FORCED]]
 *         {boolean} [options[REDUX_OPTION.SHALLOW_PARSING]]
 *         {Object}  [options[APP_OPTION.JWT]]
 * @returns {Function}
 */
function loadLegacyReduxAction (params) {
  return (dispatch, getState) => {
    params = { ...params }
    params.options = params.options || {}
    if (!params.domainListPath && !params.instanceKey) {
      params.domainListPath = getDefaultDomainListPathFromPath(params.path)
    }

    const instance = getObjectUtil(getState(), params.path)
    if (params.domainMapValue || isInstanceReduxUtil(instance)) {
      return dispatch(
        loadInstance(params)
      )
    }

    return dispatch(
      loadDomainList(params)
    )
  }
}

export {
  loadLegacyReduxAction,
  loadingLegacyReduxAction,
  loadSuccessLegacyReduxAction,
  loadErrorLegacyReduxAction
}
